const compact = (value) => String(value || "").replace(/\s+/g, " ").trim();

const stripPrefix = (value, label) => compact(value).replace(new RegExp(`^${label}\\s*[:—–-]\\s*`, "i"), "").trim();

const isStandaloneQuote = (value) => {
  const text = compact(value);
  return /^>\s*\S/.test(text)
    || /^«[^»]+»(?:\s*[—–-]\s*.+)?$/.test(text)
    || /^[“\"][^”\"]+[”\"](?:\s*[—–-]\s*.+)?$/.test(text);
};

const cleanQuote = (value) => compact(value)
  .replace(/^>\s*/, "")
  .replace(/^цитата\s*[:—–-]\s*/i, "")
  .replace(/^«|»$/g, "")
  .replace(/^[“\"]|[”\"]$/g, "")
  .trim();

// Owner QA fix: a valid AUTOMATIC headline candidate (no explicit
// "Заголовок:" label) must actually look like a headline — short and
// not a run-on sentence — or it must be left alone entirely. This is
// what stops ordinary long-form prose (e.g. a HERO leftover with no
// labels at all) from having its first paragraph, or nearly the whole
// text, swallowed whole into `headline`.
const MAX_AUTO_HEADLINE_CHARS = 140;
const MAX_AUTO_HEADLINE_SENTENCE_MARKS = 1;

const looksLikeAutoHeadline = (value) => {
  const text = compact(value);
  if (!text) return false;
  if (text.length > MAX_AUTO_HEADLINE_CHARS) return false;
  const sentenceMarks = (text.match(/[.!?]/g) || []).length;
  return sentenceMarks <= MAX_AUTO_HEADLINE_SENTENCE_MARKS;
};

export function structureEditorialStory(rawText) {
  const source = String(rawText || "").replace(/\r\n?/g, "\n").trim();
  if (!source) return { headline: "", subtitle: "", lead: "", quote: "", paragraphs: [] };

  const labelled = { headline: "", subtitle: "", lead: "", quote: "" };
  const unlabelledLines = [];
  for (const line of source.split("\n")) {
    const text = line.trim();
    if (!text) { unlabelledLines.push(""); continue; }
    if (/^заголовок\s*[:—–-]/i.test(text)) labelled.headline = stripPrefix(text, "заголовок");
    else if (/^подзаголовок\s*[:—–-]/i.test(text)) labelled.subtitle = stripPrefix(text, "подзаголовок");
    else if (/^(?:короткое\s+)?вступление\s*[:—–-]/i.test(text)) labelled.lead = text.replace(/^(?:короткое\s+)?вступление\s*[:—–-]\s*/i, "").trim();
    else if (/^цитата\s*[:—–-]/i.test(text)) labelled.quote = cleanQuote(text);
    else unlabelledLines.push(line);
  }

  let chunks = unlabelledLines.join("\n")
    .split(/\n\s*\n+/)
    .map((part) => part.trim())
    .filter(Boolean);

  // AUTO headline extraction — only when there is no explicit
  // "Заголовок:" label. The candidate is the first physical line of
  // the first chunk; it is promoted to headline only if it actually
  // looks like a headline (looksLikeAutoHeadline). If it doesn't,
  // headline stays "" and the chunk is left completely untouched — no
  // split, no removal — so a long unlabelled prose block survives
  // intact for the lead/paragraph steps below instead of being
  // silently swallowed into a giant "headline".
  if (!labelled.headline && chunks.length) {
    const firstLines = chunks[0].split(/\s*\n\s*/).filter(Boolean);
    const candidateLine = compact(firstLines[0]);

    if (looksLikeAutoHeadline(candidateLine)) {
      labelled.headline = candidateLine;
      const restLines = firstLines.slice(1);
      if (restLines.length) chunks = [compact(restLines.join(" ")), ...chunks.slice(1)];
      else chunks = chunks.slice(1);
    }
    // else: leave headline "" and chunks[0] exactly as it was.
  }

  chunks = chunks.map(compact);

  const quoteIndex = chunks.findIndex(isStandaloneQuote);
  if (!labelled.quote && quoteIndex >= 0) {
    labelled.quote = cleanQuote(chunks[quoteIndex]);
    chunks.splice(quoteIndex, 1);
  }

  if (!labelled.subtitle && chunks.length) {
    const candidate = compact(chunks[0]);
    const sentenceMarks = (candidate.match(/[.!?]/g) || []).length;
    if (candidate.length <= 140 && sentenceMarks === 0) labelled.subtitle = chunks.shift();
  }

  if (!labelled.lead && chunks.length > 1) {
    const candidate = compact(chunks[0]);
    if (candidate.length <= 360) labelled.lead = chunks.shift();
  }

  return {
    headline: labelled.headline,
    subtitle: labelled.subtitle,
    lead: labelled.lead,
    quote: labelled.quote,
    paragraphs: chunks,
  };
}

const UNIT_LABELS = { g: "г", kg: "кг", ml: "мл", l: "л", pcs: "шт." };
const displayUnit = (unit) => UNIT_LABELS[unit] || unit || "";
const displayAmount = (amount) => amount == null ? "" : String(amount).replace(".", ",");
const rowId = (groupIndex, rowIndex) => `pilot-r-${groupIndex}-${rowIndex}`;

function ingredientGroups(items = []) {
  const groups = [];
  const byTitle = new Map();
  for (const item of items) {
    const title = compact(item.section) || "Ингредиенты";
    if (!byTitle.has(title)) {
      const group = { id: `pilot-g-${groups.length}`, title, rows: [] };
      groups.push(group);
      byTitle.set(title, group);
    }
    const group = byTitle.get(title);
    group.rows.push({
      id: rowId(groups.indexOf(group), group.rows.length),
      name: compact(item.name),
      qty: displayAmount(item.amount ?? item.quantity),
      unit: displayUnit(item.unit),
      note: compact(item.note),
    });
  }
  return groups.length ? groups : [{ id: "pilot-g-0", title: "", rows: [{ id: rowId(0, 0), name: "", qty: "", unit: "", note: "" }] }];
}

const sectionKey = (value) => compact(value)
  .replace(/[«»"']/g, "")
  .replace(/\s+/g, " ")
  .toLocaleLowerCase("ru");

function technologySteps(value = "") {
  const lines = String(value || "").split(/\r?\n/).map(compact).filter(Boolean);
  const steps = [];
  let heading = "";
  let body = [];
  const flush = () => {
    if (!heading && !body.length) return;
    steps.push({
      id: `pilot-s-${steps.length}`,
      title: heading,
      instruction: body.join("\n"),
      explicit_step: false,
      time: "",
      temperature: "",
      image: { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1, alt: "" },
    });
    heading = "";
    body = [];
  };
  for (const line of lines) {
    const isHeading = line.length <= 60 && /^[А-ЯЁ0-9\s—–-]+$/.test(line) && /[А-ЯЁ]/.test(line);
    if (isHeading) { flush(); heading = line.charAt(0) + line.slice(1).toLocaleLowerCase("ru"); }
    else body.push(line);
  }
  flush();
  return steps;
}

export function collectedRecipeSections(recipe = {}) {
  const groups = Array.isArray(recipe.ingredient_groups) ? recipe.ingredient_groups : [];
  const steps = Array.isArray(recipe.technology_steps) ? recipe.technology_steps : [];
  const sections = groups.map((group, index) => ({
    id: group.id || `collected-${index}`,
    title: compact(group.title) || (groups.length === 1 ? "Рецепт" : `Компонент ${index + 1}`),
    rows: Array.isArray(group.rows) ? group.rows : [],
    technology: [],
  }));
  const unmatched = [];

  for (const step of steps) {
    const stepKey = sectionKey(step.title);
    const exact = stepKey
      ? sections.find((section) => sectionKey(section.title) === stepKey)
      : null;
    if (exact) exact.technology.push(step);
    else if (sections.length === 1) sections[0].technology.push(step);
    else unmatched.push(step);
  }

  return { sections, unmatched };
}

export function recipeRecognitionWarning(count) {
  if (count === 1) return "Проверьте 1 строку - система разобрала её неуверенно.";
  const lastTwo = count % 100;
  const last = count % 10;
  const noun = lastTwo >= 11 && lastTwo <= 14
    ? "строк"
    : last >= 2 && last <= 4 ? "строки" : "строк";
  return `Проверьте ${count} ${noun} - система разобрала их неуверенно.`;
}

const emptyExtras = () => ({ critical_points: [], professional_tips: [], storage_items: [] });

export function editorialRecipeFromRecognition(result = {}) {
  const yieldText = result.yield_quantity == null
    ? ""
    : `${displayAmount(result.yield_quantity)} ${displayUnit(result.yield_unit)}`.trim();
  return {
    eyebrow: "",
    title: compact(result.title),
    subtitle: "",
    yield: yieldText,
    mould: "",
    ingredient_groups: ingredientGroups(result.items || []),
    technology_steps: technologySteps(result.technique),
    original_text: String(result.original_text || ""),
    unresolved_lines: Array.isArray(result.unresolved_lines) ? [...result.unresolved_lines] : [],
    ...emptyExtras(),
  };
}

export function editorialRecipeFromLibrary(recipe = {}, ingredients = []) {
  const names = new Map((ingredients || []).map((item) => [item.id, item.name]));
  const items = (recipe.ingredient_lines || []).map((line) => ({
    section: line.section,
    name: names.get(line.ingredient_id) || line.name || "Ингредиент",
    quantity: line.quantity,
    unit: line.unit,
    note: line.notes,
  }));
  const yieldText = recipe.yield_quantity == null
    ? ""
    : `${displayAmount(recipe.yield_quantity)} ${displayUnit(recipe.yield_unit)}`.trim();
  return {
    eyebrow: "",
    title: compact(recipe.name),
    subtitle: "",
    yield: yieldText,
    mould: "",
    ingredient_groups: ingredientGroups(items),
    technology_steps: technologySteps(recipe.notes || recipe.technology),
    original_text: String(recipe.original_text || ""),
    unresolved_lines: [],
    ...emptyExtras(),
  };
}

export function hasEditorialRecipeContent(recipe = {}) {
  return Boolean(compact(recipe.title)
    || (recipe.ingredient_groups || []).some((group) => (group.rows || []).some((row) => compact(row.name)))
    || (recipe.technology_steps || []).some((step) => compact(step.title) || compact(step.instruction)));
}

export function hasRecipeNoteContent(item = {}) {
  return Boolean(compact(item.title) || compact(item.text));
}

export function hasRecipeStorageContent(item = {}) {
  return Boolean(compact(item.condition) || compact(item.duration) || compact(item.note));
}
